/**
 * The offers a restaurant runs.
 *
 * Four shapes cover what people actually ask for - buy two get one, a flat
 * price for a set, a percentage off a section, a combo at one price - and the
 * form shows only the numbers the chosen shape needs, because a form with every
 * field on it is how offers get saved half filled in and never fire.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { OFFER_TYPES, sortCategories, type OfferType } from '@kortex/shared';
import { api, fieldErrors } from '../lib/api';
import { useAuth } from '../lib/auth';
import { money } from '../lib/format';
import { PageHeader } from '../components/Layout';
import {
  Empty,
  ErrorState,
  Loading,
  Modal,
  Select,
  TextInput,
  useToast,
} from '../components/ui';

interface OfferMember {
  menuItemId: string;
  name: string;
  quantity: number;
}

interface Offer {
  id: string;
  name: string;
  type: OfferType;
  isActive: boolean;
  sortOrder: number;
  categoryId: string | null;
  items: OfferMember[];
  buyQty: number | null;
  freeQty: number | null;
  setQty: number | null;
  setPrice: number | null;
  percentOff: number | null;
  comboPrice: number | null;
  minTicket: number;
  maxPerTicket: number;
  startsOn: string | null;
  endsOn: string | null;
  daysOfWeek: number[];
  startMinute: number | null;
  endMinute: number | null;
}

interface MenuRow {
  id: string;
  name: string;
  category: string;
  price: number;
}

interface CategoryRow {
  id: string;
  name: string;
}

const TYPE_LABELS: Record<OfferType, string> = {
  BUY_X_GET_Y: 'Buy X, get Y free',
  SET_PRICE: 'Any N for a flat price',
  PERCENT_OFF: 'Percentage off',
  COMBO: 'Combo at one price',
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** 630 -> "10:30". Minutes from midnight is what the server keeps. */
const asTime = (minutes: number | null): string =>
  minutes === null
    ? ''
    : `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

const asMinutes = (time: string): number | null => {
  if (!time) return null;
  const [hours, minutes] = time.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
};

/** What the offer does, in the words a manager would use. */
function describe(offer: Offer, categoryName: string | null): string {
  const on = offer.categoryId
    ? (categoryName ?? 'a section')
    : offer.items.map((item) => item.name).join(' + ') || 'nothing yet';

  switch (offer.type) {
    case 'BUY_X_GET_Y':
      return `Buy ${offer.buyQty}, get ${offer.freeQty} free — ${on}`;
    case 'SET_PRICE':
      return `Any ${offer.setQty} of ${on} for ${money(offer.setPrice ?? 0)}`;
    case 'PERCENT_OFF':
      return `${offer.percentOff}% off ${on}`;
    case 'COMBO':
      return `${on} for ${money(offer.comboPrice ?? 0)}`;
    default:
      return on;
  }
}

/** When it runs, or nothing at all when it always does. */
function when(offer: Offer): string {
  const parts: string[] = [];
  if (offer.daysOfWeek.length > 0 && offer.daysOfWeek.length < 7) {
    parts.push(offer.daysOfWeek.map((day) => DAYS[day]).join(', '));
  }
  if (offer.startMinute !== null && offer.endMinute !== null) {
    parts.push(`${asTime(offer.startMinute)}–${asTime(offer.endMinute)}`);
  }
  if (offer.startsOn || offer.endsOn) {
    parts.push(`${offer.startsOn ?? '…'} to ${offer.endsOn ?? '…'}`);
  }
  return parts.join(' · ');
}

export function OffersPage() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { push, fail } = useToast();
  const [editing, setEditing] = useState<Offer | null>(null);
  const [creating, setCreating] = useState(false);

  const offers = useQuery({ queryKey: ['offers'], queryFn: () => api.get<Offer[]>('/api/offers') });
  const menu = useQuery({
    queryKey: ['menu', 'all'],
    queryFn: () => api.get<MenuRow[]>('/api/menu'),
  });
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<CategoryRow[]>('/api/categories'),
  });

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['offers'] });
    void queryClient.invalidateQueries({ queryKey: ['tickets'] });
  };

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/offers/${id}`),
    onSuccess: () => {
      push('Offer removed');
      done();
    },
    onError: fail,
  });

  const toggle = useMutation({
    mutationFn: (offer: Offer) =>
      api.put(`/api/offers/${offer.id}`, { ...toForm(offer), isActive: !offer.isActive }),
    onSuccess: () => done(),
    onError: fail,
  });

  if (offers.isLoading) return <Loading label="Reading your offers…" />;
  if (offers.error) return <ErrorState error={offers.error} onRetry={() => void offers.refetch()} />;

  const categoryName = (id: string | null) =>
    categories.data?.find((c) => c.id === id)?.name ?? null;

  return (
    <div className="stack">
      <PageHeader
        title="Offers"
        subtitle="Buy two get one, flat-price sets, happy hours and combos — applied at billing"
        actions={
          can('menu:manage') ? (
            <button className="btn primary" onClick={() => setCreating(true)}>
              + New offer
            </button>
          ) : null
        }
      />

      <div className="card">
        <div className="card-body">
          {(offers.data ?? []).length === 0 ? (
            <Empty message="No offers yet. Create one and it applies itself at the counter." />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Offer</th>
                  <th>What it does</th>
                  <th>When</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {offers.data!.map((offer) => (
                  <tr key={offer.id}>
                    <td>
                      <b>{offer.name}</b>
                      <div className="xs muted">{TYPE_LABELS[offer.type]}</div>
                    </td>
                    <td>{describe(offer, categoryName(offer.categoryId))}</td>
                    <td className="small">{when(offer) || <span className="muted">Always</span>}</td>
                    <td className="num">{offer.sortOrder}</td>
                    <td>
                      <span className={`chip ${offer.isActive ? 'ok' : ''}`}>
                        {offer.isActive ? 'Running' : 'Paused'}
                      </span>
                    </td>
                    <td className="right">
                      {can('menu:manage') ? (
                        <div className="row gap-6" style={{ justifyContent: 'flex-end' }}>
                          <button className="btn ghost sm" onClick={() => toggle.mutate(offer)}>
                            {offer.isActive ? 'Pause' : 'Resume'}
                          </button>
                          <button className="btn ghost sm" onClick={() => setEditing(offer)}>
                            Edit
                          </button>
                          <button
                            className="btn ghost sm danger"
                            onClick={() => remove.mutate(offer.id)}
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <p className="small muted">
        Offers apply themselves at billing as items are added, and a plate is only ever claimed
        by one of them. Where two could apply, the one with the lower order number goes first. A
        counter discount is worked out on whatever is left afterwards.
      </p>

      {creating || editing ? (
        <OfferModal
          offer={editing}
          menu={menu.data ?? []}
          categories={categories.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            done();
          }}
        />
      ) : null}
    </div>
  );
}

interface OfferForm {
  name: string;
  type: OfferType;
  isActive: boolean;
  sortOrder: number;
  categoryId: string | null;
  items: Array<{ menuItemId: string; quantity: number }>;
  buyQty?: number;
  freeQty?: number;
  setQty?: number;
  setPrice?: number;
  percentOff?: number;
  comboPrice?: number;
  minTicket: number;
  maxPerTicket: number;
  startsOn: string | null;
  endsOn: string | null;
  daysOfWeek: number[];
  startMinute: number | null;
  endMinute: number | null;
}

const toForm = (offer: Offer): OfferForm => ({
  name: offer.name,
  type: offer.type,
  isActive: offer.isActive,
  sortOrder: offer.sortOrder,
  categoryId: offer.categoryId,
  items: offer.items.map((item) => ({ menuItemId: item.menuItemId, quantity: item.quantity })),
  ...(offer.buyQty !== null ? { buyQty: offer.buyQty } : {}),
  ...(offer.freeQty !== null ? { freeQty: offer.freeQty } : {}),
  ...(offer.setQty !== null ? { setQty: offer.setQty } : {}),
  ...(offer.setPrice !== null ? { setPrice: offer.setPrice } : {}),
  ...(offer.percentOff !== null ? { percentOff: offer.percentOff } : {}),
  ...(offer.comboPrice !== null ? { comboPrice: offer.comboPrice } : {}),
  minTicket: offer.minTicket,
  maxPerTicket: offer.maxPerTicket,
  startsOn: offer.startsOn,
  endsOn: offer.endsOn,
  daysOfWeek: offer.daysOfWeek,
  startMinute: offer.startMinute,
  endMinute: offer.endMinute,
});

const BLANK: OfferForm = {
  name: '',
  type: 'BUY_X_GET_Y',
  isActive: true,
  sortOrder: 0,
  categoryId: null,
  items: [],
  buyQty: 2,
  freeQty: 1,
  minTicket: 0,
  maxPerTicket: 0,
  startsOn: null,
  endsOn: null,
  daysOfWeek: [],
  startMinute: null,
  endMinute: null,
};

function OfferModal({
  offer,
  menu,
  categories,
  onClose,
  onSaved,
}: {
  offer: Offer | null;
  menu: MenuRow[];
  categories: CategoryRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push, fail } = useToast();
  const [form, setForm] = useState<OfferForm>(offer ? toForm(offer) : BLANK);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof OfferForm>(key: K, value: OfferForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // A combo names its members; everything else may take a whole section.
  const mustPickItems = form.type === 'COMBO';
  const scope = form.categoryId ? 'category' : 'items';

  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, categoryId: mustPickItems ? null : form.categoryId };
      return offer ? api.put(`/api/offers/${offer.id}`, body) : api.post('/api/offers', body);
    },
    onSuccess: () => {
      push(offer ? 'Offer updated' : 'Offer created', 'ok');
      onSaved();
    },
    onError: (error) => {
      setErrors(fieldErrors(error));
      fail(error);
    },
  });

  const toggleDay = (day: number) =>
    set(
      'daysOfWeek',
      form.daysOfWeek.includes(day)
        ? form.daysOfWeek.filter((d) => d !== day)
        : [...form.daysOfWeek, day].sort(),
    );

  const addItem = (menuItemId: string) => {
    if (!menuItemId || form.items.some((item) => item.menuItemId === menuItemId)) return;
    set('items', [...form.items, { menuItemId, quantity: 1 }]);
  };

  const sortedMenu = [...menu].sort(
    (a, b) =>
      sortCategories([a.category, b.category]).indexOf(a.category) -
        sortCategories([a.category, b.category]).indexOf(b.category) || a.name.localeCompare(b.name),
  );

  return (
    <Modal
      title={offer ? `Edit ${offer.name}` : 'New offer'}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!form.name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : offer ? 'Save' : 'Create offer'}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="form-grid two">
          <TextInput
            label="What the guest sees"
            placeholder="Buy 2 dosa, get 1 free"
            value={form.name}
            error={errors['name']}
            onChange={(e) => set('name', e.target.value)}
          />
          <Select
            label="Kind of offer"
            value={form.type}
            onChange={(e) => set('type', e.target.value as OfferType)}
          >
            {OFFER_TYPES.map((type) => (
              <option key={type} value={type}>
                {TYPE_LABELS[type]}
              </option>
            ))}
          </Select>
        </div>

        {/* What it applies to */}
        {!mustPickItems ? (
          <Select
            label="Applies to"
            hint="A whole section, or the items you pick below."
            value={form.categoryId ?? ''}
            onChange={(e) => set('categoryId', e.target.value || null)}
          >
            <option value="">Just the items I pick</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                Everything in {category.name}
              </option>
            ))}
          </Select>
        ) : null}

        {mustPickItems || scope === 'items' ? (
          <div className="col gap-6">
            <label className="small b">
              {mustPickItems ? 'What is in the combo' : 'Items in the offer'}
            </label>
            {form.items.length === 0 ? (
              <span className="small muted">Nothing picked yet.</span>
            ) : (
              form.items.map((item) => (
                <div className="row gap-6" key={item.menuItemId}>
                  <span className="grow small">
                    {menu.find((m) => m.id === item.menuItemId)?.name ?? 'Unknown item'}
                  </span>
                  {mustPickItems ? (
                    <input
                      className="input"
                      type="number"
                      min={1}
                      max={20}
                      value={item.quantity}
                      style={{ width: 72, height: 30 }}
                      onChange={(e) =>
                        set(
                          'items',
                          form.items.map((row) =>
                            row.menuItemId === item.menuItemId
                              ? { ...row, quantity: Math.max(1, Number(e.target.value) || 1) }
                              : row,
                          ),
                        )
                      }
                    />
                  ) : null}
                  <button
                    className="btn ghost sm"
                    onClick={() =>
                      set(
                        'items',
                        form.items.filter((row) => row.menuItemId !== item.menuItemId),
                      )
                    }
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
            <Select label="Add an item" value="" onChange={(e) => addItem(e.target.value)}>
              <option value="">Choose a dish…</option>
              {sortedMenu.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.category} · {item.name} · {money(item.price)}
                </option>
              ))}
            </Select>
            {errors['items'] ? <span className="field-error">{errors['items']}</span> : null}
          </div>
        ) : null}

        {/* The numbers this kind of offer needs, and only those */}
        <div className="form-grid three">
          {form.type === 'BUY_X_GET_Y' ? (
            <>
              <TextInput
                label="Buy how many"
                type="number"
                min={1}
                value={form.buyQty ?? 2}
                error={errors['buyQty']}
                onChange={(e) => set('buyQty', Math.max(1, Number(e.target.value) || 1))}
              />
              <TextInput
                label="Get how many free"
                type="number"
                min={1}
                hint="The cheapest in scope go free."
                value={form.freeQty ?? 1}
                onChange={(e) => set('freeQty', Math.max(1, Number(e.target.value) || 1))}
              />
            </>
          ) : null}

          {form.type === 'SET_PRICE' ? (
            <>
              <TextInput
                label="How many items"
                type="number"
                min={2}
                value={form.setQty ?? 2}
                error={errors['setQty']}
                onChange={(e) => set('setQty', Math.max(2, Number(e.target.value) || 2))}
              />
              <TextInput
                label="Price for the set (₹)"
                type="number"
                min={0}
                hint="The dearest items go in the set."
                value={form.setPrice ?? 0}
                onChange={(e) => set('setPrice', Math.max(0, Number(e.target.value) || 0))}
              />
            </>
          ) : null}

          {form.type === 'PERCENT_OFF' ? (
            <TextInput
              label="Percentage off"
              type="number"
              min={0}
              max={100}
              value={form.percentOff ?? 10}
              error={errors['percentOff']}
              onChange={(e) => set('percentOff', Math.max(0, Number(e.target.value) || 0))}
            />
          ) : null}

          {form.type === 'COMBO' ? (
            <TextInput
              label="Combo price (₹)"
              type="number"
              min={0}
              value={form.comboPrice ?? 0}
              error={errors['comboPrice']}
              onChange={(e) => set('comboPrice', Math.max(0, Number(e.target.value) || 0))}
            />
          ) : null}

          <TextInput
            label="Only above a ticket of (₹)"
            type="number"
            min={0}
            hint="0 applies at any size."
            value={form.minTicket}
            onChange={(e) => set('minTicket', Math.max(0, Number(e.target.value) || 0))}
          />
          <TextInput
            label="Times per ticket"
            type="number"
            min={0}
            hint="0 is as many as the ticket allows."
            value={form.maxPerTicket}
            onChange={(e) => set('maxPerTicket', Math.max(0, Number(e.target.value) || 0))}
          />
          <TextInput
            label="Order"
            type="number"
            min={0}
            hint="Lower runs first, and claims plates first."
            value={form.sortOrder}
            onChange={(e) => set('sortOrder', Math.max(0, Number(e.target.value) || 0))}
          />
        </div>

        <hr className="sep" />

        {/* When it runs */}
        <div className="col gap-6">
          <label className="small b">Days it runs</label>
          <div className="row gap-6 wrap">
            {DAYS.map((label, day) => (
              <button
                key={label}
                className={`btn sm ${form.daysOfWeek.includes(day) ? 'primary' : ''}`}
                onClick={() => toggleDay(day)}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="small muted">Pick none for every day.</span>
        </div>

        <div className="form-grid two">
          <TextInput
            label="From (time of day)"
            type="time"
            value={asTime(form.startMinute)}
            onChange={(e) => set('startMinute', asMinutes(e.target.value))}
          />
          <TextInput
            label="Until"
            type="time"
            hint="Leave both blank to run all day. An end before the start runs past midnight."
            value={asTime(form.endMinute)}
            error={errors['endMinute']}
            onChange={(e) => set('endMinute', asMinutes(e.target.value))}
          />
          <TextInput
            label="Starts on"
            type="date"
            value={form.startsOn ?? ''}
            onChange={(e) => set('startsOn', e.target.value || null)}
          />
          <TextInput
            label="Ends on"
            type="date"
            value={form.endsOn ?? ''}
            error={errors['endsOn']}
            onChange={(e) => set('endsOn', e.target.value || null)}
          />
        </div>

        <Select
          label="Running now?"
          value={form.isActive ? 'yes' : 'no'}
          onChange={(e) => set('isActive', e.target.value === 'yes')}
        >
          <option value="yes">Yes — apply it at billing</option>
          <option value="no">Paused</option>
        </Select>
      </div>
    </Modal>
  );
}
